import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageCircle, RotateCcw, Send, UserRound, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { GREETING, clearChat, loadChat, saveChat, sendChat, sendHandoff } from "@/lib/siteChat";
import { ContactForm } from "@/components/chat/ContactForm";
import { cn } from "@/lib/utils";

const HIDE_ON = ["/ai-studio/chat", "/wellbeing/companion", "/admin/cc/advisor", "/onboarding", "/c/"];
const OFFLINE = "Our assistant is getting set up. Leave your name and email below and a member of our team will get back to you.";
const fresh = () => ({ session_id: null, messages: [GREETING], handoff: "none", collected: {}, handed_off: false });

const Msg = ({ m, i }) => (
  <div className={cn("max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm", m.role === "user" ? "self-end rounded-br-sm bg-[#D4AF37] text-black" : "self-start rounded-bl-sm border border-[#27272A] bg-[#18181B] text-zinc-100")} data-testid={`site-chat-msg-${i}`}>{m.content}</div>
);

export const SiteChat = () => {
  const loc = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState(() => loadChat() || fresh());
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef();

  useEffect(() => { saveChat(chat); }, [chat]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [chat.messages.length, chat.handoff, open, busy]);

  if (HIDE_ON.some((p) => loc.pathname.startsWith(p)) || loc.pathname.startsWith("/client") || user?.access_grant?.kind === "client") return null;
  const visitor = user ? { name: user.name, email: user.email, user_id: user.user_id } : chat.collected?.email ? chat.collected : null;

  const send = async (e) => {
    e?.preventDefault();
    const msg = text.trim();
    if (!msg || busy) return;
    const msgs = [...chat.messages, { role: "user", content: msg }];
    setChat((c) => ({ ...c, messages: msgs })); setText(""); setBusy(true);
    try {
      const r = await sendChat({ session_id: chat.session_id, message: msg, visitor });
      if (r.offline) setChat((c) => ({ ...c, messages: [...msgs, { role: "assistant", content: OFFLINE }], handoff: c.handed_off ? "done" : "ask_contact" }));
      else setChat((c) => ({ ...c, session_id: r.session_id || c.session_id, messages: [...msgs, { role: "assistant", content: r.reply }], collected: { ...c.collected, ...(r.collected || {}) }, handoff: c.handed_off ? "done" : r.handoff || "none", handed_off: c.handed_off || r.handoff === "done" }));
    } catch (err) {
      const known = [422, 429].includes(err.response?.status) && typeof err.response?.data?.detail === "string";
      setChat((c) => ({ ...c, messages: [...msgs, { role: "assistant", content: known ? err.response.data.detail : "Sorry, I couldn't reply just now. You can try again or ask to talk to a person." }], handoff: err.response?.status === 429 && !c.handed_off ? "ask_contact" : c.handoff }));
      if (!known) toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const handoff = async (contact) => {
    try {
      const r = await sendHandoff({ session_id: chat.session_id, contact, messages: chat.messages });
      setChat((c) => ({ ...c, collected: { ...c.collected, ...contact }, handoff: "done", handed_off: true,
        messages: [...c.messages, { role: "assistant", content: r.mailto ? `Thanks ${contact.name}! Your email app opened with this chat filled in. Just press send and our team will reply to ${contact.email}.` : `Thanks ${contact.name}! I've passed this chat to our team. They'll reply to ${contact.email} soon.` }] }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const restart = () => { clearChat(); setChat(fresh()); };

  return (
    <>
      {open && (
        <div className="fade-up fixed bottom-[156px] right-3 z-40 flex h-[min(560px,calc(100dvh-180px))] w-[370px] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-[#0F0F12]/95 shadow-2xl backdrop-blur-xl lg:bottom-24 lg:right-6" data-testid="site-chat-panel">
          <div className="flex items-center gap-3 border-b border-[#27272A] px-4 py-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#D4AF37] text-black"><MessageCircle className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-zinc-50">RPOPRO Assistant</p><p className="text-[11px] text-emerald-400">Online · replies instantly</p></div>
            <button onClick={restart} title="Start over" className="rounded-md p-1.5 text-zinc-500 hover:text-zinc-200" data-testid="site-chat-restart"><RotateCcw className="h-4 w-4" /></button>
            <button onClick={() => setOpen(false)} title="Close" className="rounded-md p-1.5 text-zinc-500 hover:text-zinc-200" data-testid="site-chat-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-4" data-testid="site-chat-messages">
            {chat.messages.map((m, i) => <Msg key={i} m={m} i={i} />)}
            {busy && <div className="self-start rounded-2xl border border-[#27272A] bg-[#18181B] px-3 py-2" data-testid="site-chat-typing"><Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" /></div>}
            {chat.handoff === "ask_contact" && !chat.handed_off && <ContactForm initial={{ ...chat.collected, ...(user ? { name: user.name, email: user.email } : {}) }} onSubmit={handoff} onCancel={() => setChat((c) => ({ ...c, handoff: "none" }))} />}
            {chat.handed_off && <p className="flex items-center gap-1.5 self-center text-[11px] text-emerald-400" data-testid="site-chat-handed-off"><CheckCircle2 className="h-3.5 w-3.5" />Our team has this chat</p>}
            <div ref={end} />
          </div>
          <form onSubmit={send} className="border-t border-[#27272A] p-3">
            <div className="flex items-end gap-2">
              <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send(e)} rows={1} maxLength={1000} placeholder="Type your question…"
                className="max-h-28 min-h-[40px] flex-1 resize-none rounded-xl border border-[#27272A] bg-[#09090B] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#D4AF37]/60" data-testid="site-chat-input" />
              <button type="submit" disabled={!text.trim() || busy} className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#D4AF37] text-black transition-opacity disabled:opacity-40" data-testid="site-chat-send"><Send className="h-4 w-4" /></button>
            </div>
            {!chat.handed_off && chat.handoff !== "ask_contact" && <button type="button" onClick={() => setChat((c) => ({ ...c, handoff: "ask_contact" }))} className="mt-2 flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#F3C649]" data-testid="site-chat-talk-human"><UserRound className="h-3 w-3" />Talk to a person</button>}
          </form>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-[92px] right-3 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37] text-black shadow-[0_8px_30px_rgba(212,175,55,0.35)] transition-transform hover:scale-105 lg:bottom-6 lg:right-6"
        data-testid="site-chat-launcher">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </>
  );
};
